/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 내비게이션 항목 클래스를 정의한다.
 *
 * @file /src/components/navigation/elements/Folder.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 5. 30.
 */
import * as parent from './Item';
import * as page from './Page';
import { Window } from '@components/window/Window';
import { Form } from '@components/form/Form';
import { Text } from '@components/form/field/text/Text';
import { Button } from '@components/button/Button';
import { Html } from '@utils/Html';
import { Format } from '@utils/Format';
import { Language } from '@utils/Language';
import { Message } from '@components/message/Message';

export interface Properties<T> extends parent.Properties<T> {
    /**
     * 폴더고유명
     */
    name: string;

    /**
     * 하위 페이지
     */
    children: page.Properties<page.Page>[];
}

export class Folder extends parent.Item {
    /**
     * 엘리먼트 타입
     */
    type = 'navigationitem';

    /**
     * 폴더고유명
     */
    name: string;

    /**
     * 하위 페이지
     */
    children: page.Page[] = [];

    /**
     * 폴더항목 클래스를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Properties<Folder> = null) {
        super(properties);

        this.name = properties.name;

        this.$getTitle().on('click', () => {
            if (this.getComponent().$getContainer().hasClass('config') === true) {
                new Window({
                    title: Language.get('components.navigation.edit_folder'),
                    width: 400,
                    closable: true,
                    modal: true,
                    items: [
                        new Form({
                            layout: 'fit',
                            border: false,
                            items: [
                                new Text({
                                    name: 'title',
                                    flex: true,
                                    value: this.title,
                                    placeholder: Language.get('components.navigation.folder_name'),
                                    listeners: {
                                        keyboard: (e, me) => {
                                            if (e.key == 'Enter') {
                                                e.preventDefault();
                                                const window = me.getParent().getParent() as Window;
                                                const button = window.getButtonAt(1);
                                                button.onClick();
                                            }
                                        },
                                    },
                                }),
                            ],
                        }),
                    ],
                    buttons: [
                        new Button({
                            text: Language.get('components.navigation.delete_folder'),
                            buttonClass: 'danger',
                            handler: (button) => {
                                const window = button.getParent().getParent() as Window;

                                Message.delete({
                                    message: Language.get('components.navigation.delete_folder_confirm'),
                                    handler: async () => {
                                        const $folder = Html.get(
                                            'aui-element[data-type=navigationitem][data-variant=folder]',
                                            this.getComponent().$getContent()
                                        );

                                        for (const child of this.children) {
                                            this.getComponent()
                                                .$getContent()
                                                .insertBefore(child.$getElement(), $folder);
                                        }

                                        await this.remove();
                                        window.close();
                                        return true;
                                    },
                                });
                            },
                        }),
                        '->',
                        new Button({
                            text: Language.get('buttons.cancel'),
                            handler: (button) => {
                                const window = button.getParent().getParent() as Window;
                                window.close();
                            },
                        }),
                        new Button({
                            text: Language.get('buttons.ok'),
                            buttonClass: 'confirm',
                            handler: async (button) => {
                                const window = button.getParent().getParent() as Window;
                                const form = window.getItemAt(0) as Form;
                                const title = (await form.getField('title').getValue()) ?? '';
                                if (title.length > 0) {
                                    this.title = title;
                                    this.$element.setData('title', title, false);
                                    this.$getTitle().html(title);
                                    window.close();
                                }
                            },
                        }),
                    ],
                }).show();
            } else {
                this.$element.toggleClass('collapsed');
            }
        });

        for (const child of properties.children) {
            this.children.push(new page.Page(child));
        }
    }

    /**
     * 내비게이션 항목을 검색한다.
     *
     * @param keyword - 키워드
     * @returns 매칭여부
     */
    search(keyword: string): boolean {
        keyword = (keyword ?? '').trim();

        if (keyword.length == 0) {
            this.$element.removeClass('unmatched');
            Html.all('aui-element', this.$element).removeClass('unmatched');
            return true;
        }

        let keycode = this.$element.getData('keycode');
        if (keycode === null) {
            keycode = Format.keycode(this.title);
            this.$element.setData('keycode', keycode, false);
        }

        let isMatched: boolean = false;
        if (keycode.includes(keyword) == true) {
            isMatched = true;
        }

        this.children.forEach((item) => {
            if (item.search(keyword) == true) {
                isMatched = true;
            }
        });

        if (isMatched == true) {
            this.$element.removeClass('unmatched');
            return true;
        } else {
            this.$element.addClass('unmatched');
            return false;
        }
    }

    /**
     * 앨리먼트를 랜더링한다.
     */
    async render(): Promise<void> {
        if (this.isRendered() == true) {
            return;
        }

        if (this.isHidden() == true) {
            this.hide();
        }

        this.$element.setData('name', this.name, false);
        this.$element.setData('title', this.title, false);

        this.$element.setAttr('data-variant', 'folder');

        const $b = Html.create('b');
        $b.append(this.$getTitle());
        $b.append(this.$getSorter());
        this.$getElement().append($b);

        const $children = Html.create('div', {
            'data-role': 'folderitems',
            'data-empty': await Language.get('components.navigation.empty_folder').getText(),
        });
        this.$getElement().append($children);

        for (const child of this.children) {
            child.setComponent(this.getComponent());
            $children.append(child.$getElement());
            child.render();
        }

        await this.onRender();
    }
}
